<script>
  import { Router, Route } from 'svelte-routing';
  import { onMount } from 'svelte';
  import { isAuthenticated } from './stores/auth.js';
  import LazyRoute from './components/LazyRoute.svelte';

  // Dynamic imports for code splitting - each page loads only when needed
  // Public pages - most commonly accessed
  const Landing = () => import('./pages/Landing.svelte');
  const Pricing = () => import('./pages/Pricing.svelte');
  const FAQ = () => import('./pages/FAQ.svelte');
  const About = () => import('./pages/About.svelte');
  const Resources = () => import('./pages/Resources.svelte');
  const Privacy = () => import('./pages/Privacy.svelte');
  const Terms = () => import('./pages/Terms.svelte');
  const Contact = () => import('./pages/Contact.svelte');

  // Content pages
  const HowItWorks = () => import('./pages/HowItWorks.svelte');
  const Features = () => import('./pages/Features.svelte');
  const Industries = () => import('./pages/Industries.svelte');
  const CaseStudies = () => import('./pages/CaseStudies.svelte');
  const Help = () => import('./pages/Help.svelte');
  const StandardsComparison = () => import('./pages/StandardsComparison.svelte');
  const LearnStandards = () => import('./pages/LearnStandards.svelte');
  const CompetitiveAdvantage = () => import('./pages/CompetitiveAdvantage.svelte');
  const TrustSecurity = () => import('./pages/TrustSecurity.svelte');

  // Auth pages - loaded only when user navigates to auth
  const Login = () => import('./pages/Login.svelte');
  const Signup = () => import('./pages/Signup.svelte');
  const ForgotPassword = () => import('./pages/ForgotPassword.svelte');

  // Protected pages - loaded only when authenticated user accesses
  const Dashboard = () => import('./pages/Dashboard.svelte');
  const Settings = () => import('./pages/Settings.svelte');
  const Billing = () => import('./pages/Billing.svelte');
  const Plans = () => import('./pages/Plans.svelte');
  const Team = () => import('./pages/Team.svelte');
  const APIAccess = () => import('./pages/APIAccess.svelte');

  // Dashboard feature pages - lazy loaded
  const Reports = () => import('./pages/Reports.svelte');
  const CompetitorAnalysis = () => import('./pages/CompetitorAnalysis.svelte');
  const IndustryBenchmarking = () => import('./pages/IndustryBenchmarking.svelte');
  const DailyPulse = () => import('./pages/DailyPulse.svelte');
  const ContentLibrary = () => import('./pages/ContentLibrary.svelte');
  const SchemaMarkup = () => import('./pages/SchemaMarkup.svelte');

  // Core features - lazy loaded
  const Websites = () => import('./pages/Websites.svelte');
  const LLMSTxtGenerator = () => import('./pages/LLMSTxtGenerator.svelte');
  const NeuralIndex = () => import('./pages/NeuralIndex.svelte');
  const AITesting = () => import('./pages/AITesting.svelte');
  const QueryIntelligence = () => import('./pages/QueryIntelligence.svelte');
  const Monitoring = () => import('./pages/Monitoring.svelte');
  const Analytics = () => import('./pages/Analytics.svelte');
  const Overview = () => import('./pages/Overview.svelte');

  // Generator pages - lazy loaded
  const AgentsJsonGenerator = () => import('./pages/AgentsJsonGenerator.svelte');
  const A2AGenerator = () => import('./pages/A2AGenerator.svelte');
  const MCPGenerator = () => import('./pages/MCPGenerator.svelte');
  const AIManifestGenerator = () => import('./pages/AIManifestGenerator.svelte');
  const PermissionsGenerator = () => import('./pages/PermissionsGenerator.svelte');

  // Additional pages - lazy loaded
  const Citations = () => import('./pages/Citations.svelte');
  const Authority = () => import('./pages/Authority.svelte');
  const Audit = () => import('./pages/Audit.svelte');
  const Notifications = () => import('./pages/Notifications.svelte');
  const AIStandards = () => import('./pages/AIStandards.svelte');
  const StandardsMonitoring = () => import('./pages/StandardsMonitoring.svelte');
  const StandardsChangelog = () => import('./pages/StandardsChangelog.svelte');
  const StandardsHealth = () => import('./pages/StandardsHealth.svelte');
  const StandardsROI = () => import('./pages/StandardsROI.svelte');

  // Error pages
  const NotFound = () => import('./pages/NotFound.svelte');

  export let url = "";

  // Scroll to top on navigation
  let lastPath = '';

  function handleNavigation() {
    const currentPath = window.location.pathname;
    if (currentPath !== lastPath) {
      lastPath = currentPath;
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }

  onMount(() => {
    handleNavigation();
    // Listen for navigation changes
    window.addEventListener('popstate', handleNavigation);
    return () => window.removeEventListener('popstate', handleNavigation);
  });
</script>

<!-- Fonts and styles are loaded in index.html for optimal performance -->

<div class="app-container">
  <!-- Render immediately, auth checks happen per-route for protected pages -->
  <Router {url}>
      <!-- Public Routes -->
      <Route path="/">
        <LazyRoute component={Landing} />
      </Route>
      <Route path="/about">
        <LazyRoute component={About} />
      </Route>
      <Route path="/pricing">
        <LazyRoute component={Pricing} />
      </Route>
      <Route path="/resources">
        <LazyRoute component={Resources} />
      </Route>
      <Route path="/faq">
        <LazyRoute component={FAQ} />
      </Route>
      <Route path="/contact">
        <LazyRoute component={Contact} />
      </Route>
      <Route path="/privacy">
        <LazyRoute component={Privacy} />
      </Route>
      <Route path="/terms">
        <LazyRoute component={Terms} />
      </Route>

      <!-- Content Pages -->
      <Route path="/how-it-works">
        <LazyRoute component={HowItWorks} />
      </Route>
      <Route path="/features">
        <LazyRoute component={Features} />
      </Route>
      <Route path="/industries">
        <LazyRoute component={Industries} />
      </Route>
      <Route path="/case-studies">
        <LazyRoute component={CaseStudies} />
      </Route>
      <Route path="/help">
        <LazyRoute component={Help} />
      </Route>
      <Route path="/standards-comparison">
        <LazyRoute component={StandardsComparison} />
      </Route>
      <Route path="/learn-standards">
        <LazyRoute component={LearnStandards} />
      </Route>
      <Route path="/competitive-advantage">
        <LazyRoute component={CompetitiveAdvantage} />
      </Route>
      <Route path="/trust-security">
        <LazyRoute component={TrustSecurity} />
      </Route>

      <!-- Auth Routes -->
      <Route path="/login">
        <LazyRoute component={Login} />
      </Route>
      <Route path="/signup">
        <LazyRoute component={Signup} />
      </Route>
      <Route path="/forgot-password">
        <LazyRoute component={ForgotPassword} />
      </Route>

      <!-- Protected Routes -->
      <Route path="/dashboard">
        {#if $isAuthenticated}
          <LazyRoute component={Dashboard} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/settings">
        {#if $isAuthenticated}
          <LazyRoute component={Settings} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/billing">
        {#if $isAuthenticated}
          <LazyRoute component={Billing} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/plans">
        {#if $isAuthenticated}
          <LazyRoute component={Plans} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/team">
        {#if $isAuthenticated}
          <LazyRoute component={Team} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/api">
        {#if $isAuthenticated}
          <LazyRoute component={APIAccess} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <!-- Dashboard Feature Pages - Analysis & Reporting -->
      <Route path="/reports">
        {#if $isAuthenticated}
          <LazyRoute component={Reports} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/competitor-analysis">
        {#if $isAuthenticated}
          <LazyRoute component={CompetitorAnalysis} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/industry-benchmarking">
        {#if $isAuthenticated}
          <LazyRoute component={IndustryBenchmarking} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/daily-pulse">
        {#if $isAuthenticated}
          <LazyRoute component={DailyPulse} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/content-library">
        {#if $isAuthenticated}
          <LazyRoute component={ContentLibrary} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/schema-markup">
        {#if $isAuthenticated}
          <LazyRoute component={SchemaMarkup} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <!-- Dashboard Feature Pages - Core Features -->
      <Route path="/websites">
        {#if $isAuthenticated}
          <LazyRoute component={Websites} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/llms-txt-generator">
        {#if $isAuthenticated}
          <LazyRoute component={LLMSTxtGenerator} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/neural-index">
        {#if $isAuthenticated}
          <LazyRoute component={NeuralIndex} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/ai-testing">
        {#if $isAuthenticated}
          <LazyRoute component={AITesting} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/query-intelligence">
        {#if $isAuthenticated}
          <LazyRoute component={QueryIntelligence} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/monitoring">
        {#if $isAuthenticated}
          <LazyRoute component={Monitoring} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/analytics">
        {#if $isAuthenticated}
          <LazyRoute component={Analytics} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/overview">
        {#if $isAuthenticated}
          <LazyRoute component={Overview} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/citations">
        {#if $isAuthenticated}
          <LazyRoute component={Citations} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/authority">
        {#if $isAuthenticated}
          <LazyRoute component={Authority} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/audit">
        {#if $isAuthenticated}
          <LazyRoute component={Audit} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/notifications">
        {#if $isAuthenticated}
          <LazyRoute component={Notifications} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/ai-standards">
        {#if $isAuthenticated}
          <LazyRoute component={AIStandards} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/standards-monitoring">
        {#if $isAuthenticated}
          <LazyRoute component={StandardsMonitoring} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/standards-changelog">
        {#if $isAuthenticated}
          <LazyRoute component={StandardsChangelog} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/standards-health">
        {#if $isAuthenticated}
          <LazyRoute component={StandardsHealth} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/standards-roi">
        {#if $isAuthenticated}
          <LazyRoute component={StandardsROI} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <!-- Generator Pages for AI Standards -->
      <Route path="/agents-json-generator">
        {#if $isAuthenticated}
          <LazyRoute component={AgentsJsonGenerator} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/a2a-generator">
        {#if $isAuthenticated}
          <LazyRoute component={A2AGenerator} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/mcp-generator">
        {#if $isAuthenticated}
          <LazyRoute component={MCPGenerator} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/ai-manifest-generator">
        {#if $isAuthenticated}
          <LazyRoute component={AIManifestGenerator} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <Route path="/permissions-generator">
        {#if $isAuthenticated}
          <LazyRoute component={PermissionsGenerator} />
        {:else}
          <LazyRoute component={Login} />
        {/if}
      </Route>

      <!-- 404 Fallback -->
      <Route>
        <LazyRoute component={NotFound} />
      </Route>
    </Router>
</div>

<style>
  .app-container {
    min-height: 100vh;
  }
</style>
